import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';

import useDebounce from '../../../../../components/UseDebounce';
import { BTN_COLOR, THEME_COLOR3, THEME_COLOR4, THEME_COLOR5 } from '../../../../../constants';
import type { TMenuItemSlim } from '../types/menu-types';
import type { TModifier } from '../../modifiers/types';
import { buildModifierRecords } from '../utils/menu-item-utils';

type TModifierPickerDialogProps = {
  open: boolean;
  currentIds: string[];
  allModifiers: TModifier[];
  isLoadingModifiers?: boolean;
  menuItems: TMenuItemSlim[];
  categories: { _id: string; name: string }[];
  onSubmit: (ids: string[]) => void;
  onCancel: () => void;
};

const ModifierPickerDialog = ({
  open,
  currentIds,
  allModifiers,
  isLoadingModifiers = false,
  menuItems,
  categories,
  onSubmit,
  onCancel,
}: TModifierPickerDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(() => [...currentIds]);
  const [search, setSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const currentIdsRef = useRef(currentIds);
  currentIdsRef.current = currentIds;

  const resetState = () => {
    setSelectedIds([...currentIdsRef.current]);
    setSearch('');
    setShowSelectedOnly(false);
  };

  useEffect(() => {
    if (!open) return;
    resetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const debouncedSearch = useDebounce(search, 200);

  const modifierRecords = useMemo(
    () => buildModifierRecords(allModifiers, menuItems, categories),
    [allModifiers, menuItems, categories]
  );

  const filteredRecords = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return modifierRecords.filter((record) => {
      if (showSelectedOnly && !selectedIds.includes(record._idModifier)) return false;
      if (!q) return true;
      return (
        record.header.toLowerCase().includes(q) ||
        record.optionItems.some((item) => item.name.toLowerCase().includes(q)) ||
        record.attachedCategoryNames.some((name) => name.toLowerCase().includes(q))
      );
    });
  }, [modifierRecords, debouncedSearch, showSelectedOnly, selectedIds]);

  const toggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const handleApply = () => {
    onSubmit(selectedIds);
  };

  const emptyMessage = showSelectedOnly
    ? debouncedSearch.trim()
      ? 'No selected modifiers match your search.'
      : 'No modifiers selected yet.'
    : debouncedSearch.trim()
      ? 'No modifiers match your search.'
      : 'No modifiers found for this restaurant.';

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={s.title}>Add existing modifiers</DialogTitle>

      <Box sx={s.searchWrapper}>
        <Box sx={s.searchRow}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by modifier, item or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: THEME_COLOR3 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: '10px' },
            }}
          />
          <Button
            type="button"
            size="small"
            variant={showSelectedOnly ? 'contained' : 'outlined'}
            onClick={() => setShowSelectedOnly((v) => !v)}
            sx={showSelectedOnly ? s.filterBtnActive : s.filterBtn}
          >
            {showSelectedOnly ? 'Show All' : 'Show Selected'}
          </Button>
        </Box>
        <Typography sx={s.counter}>
          {filteredRecords.length} result{filteredRecords.length !== 1 ? 's' : ''} &bull;{' '}
          {selectedIds.length} selected
        </Typography>
      </Box>

      <DialogContent dividers sx={s.content}>
        {isLoadingModifiers ? (
          <Box sx={s.centred}>
            <CircularProgress size={24} sx={{ color: BTN_COLOR }} />
          </Box>
        ) : filteredRecords.length === 0 ? (
          <Typography sx={s.empty}>{emptyMessage}</Typography>
        ) : (
          filteredRecords.map((record) => {
            const isSelected = selectedIds.includes(record._idModifier);
            const previewNames = record.optionItems.slice(0, 3).map((i) => i.name);
            const remaining = Math.max(record.optionItems.length - previewNames.length, 0);

            return (
              <Box
                key={record._idModifier}
                sx={{ ...s.row, ...(isSelected ? s.rowSelected : {}) }}
                onClick={() => toggle(record._idModifier)}
              >
                {isSelected ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: BTN_COLOR, flexShrink: 0 }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: 18, color: THEME_COLOR4, flexShrink: 0 }}
                  />
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={s.headerRow}>
                    <Typography sx={{ ...s.modifierHeader, ...(isSelected ? s.headerSelected : {}) }} noWrap>
                      {record.header}
                    </Typography>
                    <Box sx={s.badges}>
                      <Box sx={s.badge}>
                        {record.optionItems.length} item{record.optionItems.length !== 1 ? 's' : ''}
                      </Box>
                      {record.required && <Box sx={s.badge}>Required</Box>}
                      {record.multiSelection && <Box sx={s.badge}>Multi</Box>}
                    </Box>
                  </Box>

                  {previewNames.length > 0 && (
                    <Typography sx={s.preview}>
                      {previewNames.join(', ')}
                      {remaining > 0 ? ` +${remaining} more` : ''}
                    </Typography>
                  )}

                  {record.attachedCategoryNames.length > 0 && (
                    <Typography sx={s.usedIn}>
                      Used in: {record.attachedCategoryNames.join(', ')}
                    </Typography>
                  )}
                </Box>

                <Typography sx={{ ...s.selectLabel, ...(isSelected ? s.selectLabelActive : {}) }}>
                  {isSelected ? 'Selected' : 'Select'}
                </Typography>
              </Box>
            );
          })
        )}
      </DialogContent>

      <DialogActions sx={s.actions}>
        <Button variant="outlined" onClick={handleCancel} sx={s.cancelBtn}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} sx={s.applyBtn}>
          Apply ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifierPickerDialog;

const s = {
  title: { fontFamily: 'GilroyBold', fontSize: 16, pb: 1 },
  searchWrapper: { px: 3, pb: 1.5, display: 'flex', flexDirection: 'column' as const, gap: 1 },
  searchRow: { display: 'flex', gap: 1, alignItems: 'center' },
  filterBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    borderColor: THEME_COLOR4,
    color: '#000',
    borderRadius: '8px',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    width: 128,
    height: 40,
    '&:hover': { borderColor: '#000', backgroundColor: 'transparent' },
  },
  filterBtnActive: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    backgroundColor: BTN_COLOR,
    color: '#FFF',
    borderRadius: '8px',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    width: 128,
    height: 40,
    '&:hover': { backgroundColor: BTN_COLOR },
  },
  counter: { fontFamily: 'GilroyRegular', fontSize: 12, color: THEME_COLOR3 },
  content: { p: 0, maxHeight: 420, display: 'flex', flexDirection: 'column' as const },
  centred: { display: 'flex', justifyContent: 'center', py: 5 },
  empty: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: THEME_COLOR3,
    textAlign: 'center' as const,
    py: 4,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    px: 2,
    py: 1.5,
    cursor: 'pointer',
    borderBottom: `1px solid ${THEME_COLOR4}`,
    '&:last-of-type': { borderBottom: 0 },
    '&:hover': { backgroundColor: '#FAFAFA' },
    transition: 'background-color 0.1s',
  },
  rowSelected: {
    backgroundColor: `${THEME_COLOR5}25`,
    '&:hover': { backgroundColor: `${THEME_COLOR5}25` },
  },
  headerRow: { display: 'flex', flexWrap: 'wrap' as const, alignItems: 'center', gap: 0.75 },
  modifierHeader: { fontFamily: 'GilroySemiBold', fontSize: 13, color: '#000' },
  headerSelected: { color: BTN_COLOR },
  badges: { display: 'flex', flexWrap: 'wrap' as const, gap: 0.5 },
  badge: {
    display: 'inline-flex',
    px: 0.75,
    py: 0.25,
    borderRadius: '4px',
    backgroundColor: '#F5F5F5',
    fontFamily: 'GilroyRegular',
    fontSize: 11,
    color: THEME_COLOR3,
  },
  preview: { fontFamily: 'GilroyRegular', fontSize: 11, color: THEME_COLOR5, mt: 0.5 },
  usedIn: { fontFamily: 'GilroyRegular', fontSize: 11, color: THEME_COLOR5, mt: 0.25 },
  selectLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    color: THEME_COLOR3,
    flexShrink: 0,
    alignSelf: 'center',
  },
  selectLabelActive: { color: BTN_COLOR },
  actions: { px: 3, py: 2, gap: 1 },
  cancelBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    borderColor: THEME_COLOR4,
    color: '#000',
    borderRadius: '8px',
    '&:hover': { borderColor: '#000', backgroundColor: 'transparent' },
  },
  applyBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    backgroundColor: BTN_COLOR,
    color: '#FFF',
    borderRadius: '8px',
    '&:hover': { backgroundColor: BTN_COLOR },
  },
};
